import { Avatar, Box, Card, Grid, LinearProgress, Skeleton, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import { getDistributorLeaderboard } from '../../redux-store/actions/application';
import { RootStateType } from '../../redux-store/reducers';
import { DistributorLeaderboardResponse } from '../../redux-store/types/api-types';
import { formatCurrency } from '../../utils/utilityFunctions';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

export const distributorWise: DistributorLeaderboardResponse = {
  days: 7,
  data: [],
};

type DayFilter = 7 | 15 | 30;

const LEADERBOARD_LIMIT = 5;

const getInitials = (name: string): string => {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return 'NA';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
};

export default function TopDistributorRM(): JSX.Element {
  const dispatch = useDispatch();
  const history = useHistory();
  const [dayFilter, setDayFilter] = useState<DayFilter>(7);
  const [isLoading, setIsLoading] = useState(true);
  const topDistributorData = useSelector((store: RootStateType) => store.topDistributors);

  const distributors = (topDistributorData.data || [])
    .filter((dis) => dis.name && (dis.commitmentAmount || 0) > 0)
    .slice(0, LEADERBOARD_LIMIT);

  useEffect(() => {
    let componentIsAlive = true;
    (async function () {
      try {
        setIsLoading(true);
        await dispatch(getDistributorLeaderboard({ days: dayFilter, limit: LEADERBOARD_LIMIT }));
        if (!componentIsAlive) {
          return;
        }
      } catch (e) {
        console.error((e as Error).message);
      } finally {
        if (componentIsAlive) {
          setIsLoading(false);
        }
      }
    })();
    return () => {
      componentIsAlive = false;
    };
  }, [dayFilter]);

  const dayFilters: { label: string; value: DayFilter }[] = [
    { label: 'Last 7 Days', value: 7 },
    { label: 'Last 15 Days', value: 15 },
    { label: 'Last 30 Days', value: 30 },
  ];

  return (
    <Grid item xs={12} lg={5} sx={{ minWidth: 0, maxWidth: '100%' }}>
      <Card
        elevation={0}
        sx={{
          height: '100%',
          p: { xs: 2, sm: 2.5 },
          borderRadius: 3,
          border: '1px solid #E8ECF0',
          backgroundColor: 'transparent',
          backgroundImage: 'linear-gradient(168.53deg, #FFFFFF 26.61%, #F0FDFA 90.78%)',
          backgroundRepeat: 'no-repeat',
          backgroundSize: 'cover',
          fontFamily: 'Roboto, sans-serif',
          width: '100%',
          maxWidth: '100%',
          boxSizing: 'border-box',
          overflow: 'hidden',
          '& .MuiTypography-root': {
            fontFamily: 'Roboto, sans-serif',
          },
        }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 2,
          }}>
          <Typography
            sx={{
              fontFamily: 'Roboto, sans-serif',
              fontStyle: 'normal',
              fontSize: '20px',
              fontWeight: 600,
              lineHeight: 1,
              letterSpacing: 0,
              color: '#111927',
            }}>
            Distributor Leaderboard
          </Typography>
          <Box
            component="button"
            type="button"
            onClick={() => history.push('/user-statistics')}
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              border: 'none',
              background: 'transparent',
              cursor: 'pointer',
              color: '#006EB7',
              fontSize: 14,
              fontWeight: 600,
              lineHeight: 1.2,
              p: 0,
              fontFamily: 'Roboto, sans-serif',
            }}>
            View All
            <ChevronRightIcon sx={{ fontSize: 18 }} />
          </Box>
        </Box>

        <Box
          sx={{
            display: 'flex',
            gap: 1,
            mb: 2.5,
            p: 0.5,
            borderRadius: 2,
            background: '#F4F6F8',
            flexWrap: 'wrap',
          }}>
          {dayFilters.map((filter) => (
            <Box
              key={filter.value}
              component="button"
              type="button"
              aria-pressed={dayFilter === filter.value}
              onClick={() => setDayFilter(filter.value)}
              sx={{
                flex: 1,
                minWidth: 90,
                border: 'none',
                cursor: 'pointer',
                borderRadius: 1.5,
                py: 1,
                px: 1,
                fontSize: 14,
                fontWeight: 600,
                fontFamily: 'Roboto, sans-serif',
                background: dayFilter === filter.value ? '#fff' : 'transparent',
                color: dayFilter === filter.value ? '#1A2B3C' : '#7A8A9A',
                boxShadow: dayFilter === filter.value ? '0 1px 4px rgba(26, 43, 60, 0.08)' : 'none',
                transition: 'all 0.2s ease',
              }}>
              {filter.label}
            </Box>
          ))}
        </Box>

        {isLoading ? (
          <Box>
            {[1, 2, 3, 4, 5].map((i) => (
              <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                <Skeleton variant="circular" width={40} height={40} />
                <Box sx={{ flex: 1 }}>
                  <Skeleton width="40%" height={18} />
                  <Skeleton width="100%" height={8} sx={{ mt: 1 }} />
                </Box>
                <Skeleton width={60} height={18} />
              </Box>
            ))}
          </Box>
        ) : distributors.length === 0 ? (
          <Typography
            sx={{
              fontFamily: 'Roboto, sans-serif',
              fontSize: 15,
              fontWeight: 500,
              color: '#7A8A9A',
              textAlign: 'center',
              py: 8,
            }}>
            No Data Available
          </Typography>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {distributors.map((row) => {
              const progress = Math.min(Math.max(row.progressPercent ?? 0, 0), 100);
              return (
                <Box
                  key={row.distributorId ?? `${row.name}-${row.rank}`}
                  sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
                  <Avatar
                    sx={{
                      width: 40,
                      height: 40,
                      fontSize: 13,
                      fontWeight: 700,
                      fontFamily: 'Roboto, sans-serif',
                      background: 'linear-gradient(180deg, #151A20 0%, #2057A6 100%)',
                      color: '#fff',
                      flexShrink: 0,
                    }}>
                    {getInitials(row.name || 'NA')}
                  </Avatar>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 1,
                        mb: 0.75,
                        minWidth: 0,
                      }}>
                      <Typography
                        sx={{
                          fontFamily: 'Roboto, sans-serif',
                          fontSize: 14,
                          fontWeight: 500,
                          color: '#6C737F',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          minWidth: 0,
                        }}>
                        {row.name || 'N/A'}
                      </Typography>
                      <Typography
                        sx={{
                          fontFamily: 'Roboto, sans-serif',
                          fontSize: 14,
                          fontWeight: 700,
                          color: '#111927',
                          whiteSpace: 'nowrap',
                          flexShrink: 0,
                        }}>
                        {`${formatCurrency(row.commitmentAmount || 0)}.`}
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={progress}
                      sx={{
                        width: '100%',
                        height: 8,
                        borderRadius: 4,
                        backgroundColor: '#E8F0F0',
                        '& .MuiLinearProgress-bar': {
                          borderRadius: 4,
                          backgroundColor: '#0E719D',
                        },
                      }}
                    />
                  </Box>
                </Box>
              );
            })}
          </Box>
        )}
      </Card>
    </Grid>
  );
}
